import type { JourneyHandle, JourneyStepHandle } from "@wishlink/applens-web";
import type {
  JourneyEntry,
  JourneyEndReason,
  OperationCompletion,
} from "@wishlink/applens-core";
import { classifyOgCompletion, submitOgWaitMs } from "./diyOgPrefetch";

export function submitCompletion(response: any): OperationCompletion {
  if (response?.data?.success === true) return { outcome: "success" };
  const status =
    typeof response?.error?.status === "number"
      ? response.error.status
      : response?.error?.originalStatus;
  const errorCode =
    status === 429
      ? "rate_limited"
      : status === 401 || status === 403
      ? "auth"
      : typeof status === "number" && status >= 500 && status <= 599
      ? "server_error"
      : response?.data
      ? "backend_soft_fail"
      : typeof status === "number"
      ? undefined
      : "network";
  return { outcome: "error", errorCode };
}

/** Only bounded UI bookkeeping. SDK owns spans, parents, deadlines and limits. */
export function createDiyJourney(deps: {
  start(entry: JourneyEntry): JourneyHandle | undefined;
  now(): number;
  schedule(ms: number, work: () => void): () => void;
}) {
  let pendingEntry: JourneyEntry | undefined;
  let current: ReturnType<typeof make> | undefined;
  function make(entry: JourneyEntry) {
    const root = deps.start(entry);
    if (!root) return;
    let closed = false,
      committed = false,
      ready = false,
      valid = false,
      invalid = false;
    let errors = 0,
      lastError: string | undefined;
    let screen: JourneyStepHandle | undefined,
      link: JourneyStepHandle | undefined;
    let og: { step: JourneyStepHandle; issued: number } | undefined;
    // Registered SDK deadline fires first. This timer releases UI bookkeeping only.
    const cancelCleanup = deps.schedule(600000, () => {
      closed = true;
      if (current === handle) current = undefined;
    });
    const finish = (reason: JourneyEndReason) => {
      if (closed) return;
      if (reason !== "acknowledged") {
        screen?.finish({ outcome: "cancelled" });
        screen = undefined;
        link?.finish(
          { outcome: "cancelled", errorCode: lastError },
          { validationErrors: errors }
        );
        link = undefined;
        og?.step.finish({ outcome: "cancelled" });
        og = undefined;
      }
      if (
        root.finish(
          { outcome: reason === "acknowledged" ? "deferred" : "cancelled" },
          { endReason: reason }
        )
      ) {
        closed = true;
        cancelCleanup();
        if (current === handle) current = undefined;
      }
    };
    const endLink = () => {
      if (ready && valid && link) {
        link.finish(
          { outcome: "success", errorCode: lastError },
          { validationErrors: errors }
        );
        link = undefined;
      }
    };
    const handle = {
      get closed() {
        return closed;
      },
      get committed() {
        return committed;
      },
      commit() {
        if (closed || committed) return;
        committed = true;
        screen = root.startStep("screen_ready");
      },
      ready() {
        if (closed || ready || !committed) return;
        ready = true;
        screen?.finish({ outcome: "success" });
        screen = undefined;
        link = root.startStep("link_entry");
        endLink();
      },
      validity(nonempty: boolean, isValid: boolean, errorCode?: string) {
        if (closed) return;
        valid = nonempty && isValid;
        const nextInvalid = nonempty && !isValid;
        if (nextInvalid && link) {
          if (!invalid) errors = Math.min(20, errors + 1);
          lastError = errorCode;
        }
        invalid = nextInvalid;
        endLink();
      },
      issueOg() {
        if (closed) return;
        og?.step.finish({ outcome: "cancelled" });
        const record = {
          step: root.startStep("og_prefetch"),
          issued: deps.now(),
        };
        og = record;
        return (hasData: boolean) => {
          if (closed || og !== record) return;
          const result = classifyOgCompletion(
            hasData,
            record.issued,
            deps.now()
          );
          if (result) record.step.finish(result);
          og = undefined;
        };
      },
      submit() {
        if (closed) return;
        const tapped = deps.now();
        const step = root.startStep("submit");
        let wait: number | undefined,
          done = false;
        return {
          waitedUntil(at?: number) {
            wait = submitOgWaitMs(tapped, at);
          },
          finish(response: unknown, prefetchFailed = false) {
            if (closed || done) return;
            done = true;
            const completion: OperationCompletion = prefetchFailed
              ? { outcome: "error", errorCode: "no_data" }
              : submitCompletion(response);
            step.finish(completion, { ogWaitMs: wait });
            if (completion.outcome === "success") {
              // A later link may have superseded the submitted prefetch; acknowledgement wins.
              og?.step.finish({ outcome: "cancelled" });
              og = undefined;
              screen?.finish({ outcome: "cancelled" });
              screen = undefined;
              link?.finish(
                { outcome: "cancelled", errorCode: lastError },
                { validationErrors: errors }
              );
              link = undefined;
              finish("acknowledged");
            }
          },
        };
      },
      loader(action: "start" | "end") {
        if (!closed) root.loader(action);
      },
      finish,
      discard() {
        closed = true;
        cancelCleanup();
      },
    };
    return handle;
  }
  return {
    start(entry: JourneyEntry = "other") {
      if (!current || current.closed) current = make(entry);
      return current;
    },
    capture: () => current,
    prepare(entry: JourneyEntry) {
      pendingEntry = entry;
    },
    route(path: string) {
      if (path === "/new-product") {
        const entry = pendingEntry ?? "other";
        pendingEntry = undefined;
        this.start(entry)?.commit();
      } else {
        pendingEntry = undefined;
        current?.finish("navigate_away");
      }
    },
    disable() {
      current?.discard();
      current = undefined;
    },
  };
}
export type DiyHandle = NonNullable<
  ReturnType<ReturnType<typeof createDiyJourney>["capture"]>
>;
